import { Routes, Route, useLocation, Navigate, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";

import Header from "./components/Header/Header";
import Footer from "./components/Footer/Footer";
import CartSidebar from "./components/CartSidebar/CartSidebar";
import CartRedirect from "./components/CartRedirect/CartRedirect";
import ScrollToTop from "./components/ScrollToTop/ScrollToTop";
import AgeVerification from "./components/AgeVerification/AgeVerification";

import Home from "./pages/Home/Home";
import Login from "./pages/Login/Login";
import Products from "./pages/Products/Products";
import ProductDetails from "./pages/ProductDetails/ProductDetails";
import Checkout from "./pages/Checkout/Checkout";
import PaymentSuccess from "./pages/PaymentSuccess/PaymentSuccess";
import PaymentFailed from "./pages/PaymentFailed/PaymentFailed";
import Subscribe from "./pages/Subscribe/Subscribe";
import StoreLocator from "./pages/StoreLocator/StoreLocator";
import LabReports from "./pages/LabReports/LabReports";
import About from "./pages/About/About";
import FAQ from "./pages/FAQ/FAQ";
import Contact from "./pages/Contact/Contact";
import Account, { ACCOUNT_SECTIONS, ORDER_DETAIL_PATH } from "./pages/Account/Account";
import TermsOfService from "./pages/TermsOfService/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy/PrivacyPolicy";
import RefundPolicy from "./pages/RefundPolicy/RefundPolicy";
import CancellationPolicy from "./pages/CancellationPolicy/CancellationPolicy";
import NotFound from "./pages/NotFound/NotFound";

export default function UserApp() {
  const location = useLocation();
  const navigate = useNavigate();
  const isSignupPage = location.pathname === '/signup';
  const isCheckoutPage = location.pathname.startsWith('/checkout');
  // sessionStorage, not localStorage - the gate must ask again on every new
  // visit, and sessionStorage is dropped when the browser session ends.
  // Read on init, not in an effect - an effect runs after the first paint, so
  // the modal flashed on every reload before it could be told to stay hidden
  const [isAgeVerified, setIsAgeVerified] = useState(() => {
    try {
      return sessionStorage.getItem('ageVerified') === 'true';
    } catch {
      return false;
    }
  });

  // Normalize double slashes in URL
  useEffect(() => {
    if (location.pathname.includes('//')) {
      const normalizedPath = location.pathname.replace(/\/+/g, '/');
      navigate(normalizedPath + location.search, { replace: true });
    }
  }, [location.pathname, location.search, navigate]);

  const handleAgeVerified = () => {
    setIsAgeVerified(true);
    try {
      sessionStorage.setItem('ageVerified', 'true');
      // Clear the old permanent flag so returning visitors who accepted
      // before this change still get asked on their next visit
      localStorage.removeItem('ageVerified');
    } catch {
      // Storage blocked - the gate will simply ask again next visit
    }
  };

  return (
    <>
      <ScrollToTop />
      
      {/* Age Verification Modal */}
      {!isAgeVerified && <AgeVerification onVerified={handleAgeVerified} />}

      {!isSignupPage && isAgeVerified && <Header />}

      <CartSidebar />

      <div className="main-content">
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Login />} />
          <Route path="/" element={<Home />} />
          <Route path="/products" element={<Products />} />
          <Route path="/product/:id" element={<ProductDetails />} />
          <Route path="/cart" element={<CartRedirect />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/checkout/:uuid" element={<Checkout />} />
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route path="/success" element={<PaymentSuccess />} />
          <Route path="/paypal-success" element={<PaymentSuccess />} />
          <Route path="/payment-failed" element={<PaymentFailed />} />
          <Route path="/failed" element={<PaymentFailed />} />
          <Route path="/paypal-failed" element={<PaymentFailed />} />
          <Route path="/cancel" element={<Navigate to="/checkout" replace />} />
          <Route path="/subscribe" element={<Subscribe />} />
          <Route path="/stores" element={<StoreLocator />} />
          <Route path="/labs" element={<LabReports />} />
          <Route path="/about" element={<About />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/contact" element={<Contact />} />
          {/* One route per account section - /addresses, /orders and friends
              each get a real URL instead of /profile?section=... */}
          {ACCOUNT_SECTIONS.map((section) => (
            <Route key={section.id} path={section.path} element={<Account />} />
          ))}
          <Route path={ORDER_DETAIL_PATH} element={<Account />} />
          <Route path="/address" element={<Navigate to="/addresses" replace />} />
          <Route path="/account" element={<Navigate to="/profile" replace />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />
          <Route path="/cancellation-policy" element={<CancellationPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          {/* Catch-all: without this an unknown URL rendered an empty page
              between the header and footer with no explanation */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </div>

      {!isSignupPage && !isCheckoutPage && isAgeVerified && <Footer />}
    </>
  );
}


